<script setup lang="ts">
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Person {
    name?: string;
    email?: string;
    type?: string;
}

interface CommunicationEvent {
    sender?: Person;
    subject?: string;
    text?: string;
    date_sent?: string;
}

const props = withDefaults(
    defineProps<{
        event: CommunicationEvent;
        isHit?: boolean;
        recipients?: Person[];
    }>(),
    {
        isHit: false,
        recipients: () => [],
    },
);

const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
};

const getInitials = (name: string | null | undefined) => {
    if (!name) return '?';
    const parts = name.split(' ').filter((p) => p.length > 0);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
};

const getCleanBody = (text: string | null | undefined) => {
    if (!text) return '';

    const lines = text.split(/\r?\n/);
    const cleanLines: string[] = [];
    let headersDone = false;

    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const trimmedLine = line.trim();

        if (!headersDone) {
            if (trimmedLine.match(/^(From|Sent|To|Cc|Bcc|Subject|Date)\s*:/i)) {
                continue;
            }
            if (trimmedLine.match(/^On .+ wrote:$/i)) {
                continue;
            }
            if (trimmedLine.match(/^>?\s*On .+ wrote:$/i)) {
                cleanLines.push(line);
                headersDone = true;
                continue;
            }
            if (trimmedLine === '' && cleanLines.length === 0) {
                continue;
            }
            headersDone = true;
        }

        cleanLines.push(line);
    }

    while (cleanLines.length > 0 && cleanLines[0].trim() === '') {
        cleanLines.shift();
    }
    while (cleanLines.length > 0 && cleanLines[cleanLines.length - 1].trim() === '') {
        cleanLines.pop();
    }

    return cleanLines.join('\n');
};

const groupedRecipients = () => {
    const groups: { To: Person[]; Cc: Person[]; Bcc: Person[] } = { To: [], Cc: [], Bcc: [] };
    if (props.recipients) {
        props.recipients.forEach((r) => {
            const type = r.type || 'To';
            if (type === 'To' || type === 'Cc' || type === 'Bcc') {
                groups[type].push(r);
            } else {
                groups.To.push(r);
            }
        });
    }
    return groups;
};

// Exposed for compatibility but not currently used in template
void groupedRecipients;
</script>

<template>
    <div
        :class="[
            'email-item',
            {
                'email-hit': isHit,
                'email-non-hit': !isHit,
            },
        ]"
    >
        <div class="email-header">
            <div class="email-avatar" :class="{ 'avatar-hit': isHit }">
                {{ getInitials(event.sender?.name) }}
            </div>

            <div class="email-meta">
                <div class="email-sender-row">
                    <span class="email-sender-name">{{
                        event.sender?.name || t('references.unknownSender')
                    }}</span>
                    <span class="email-date">{{ formatDate(event.date_sent) }}</span>
                </div>
                <div class="email-sender-email">{{ event.sender?.email || '' }}</div>

                <div v-if="recipients && recipients.length > 0" class="email-recipients">
                    <span class="email-recipients-label">{{ t('references.toLabel') }}</span>
                    <span class="email-recipients-list">
                        {{ recipients.map((r) => r.name || r.email).join(', ') }}
                    </span>
                </div>
            </div>
        </div>

        <div v-if="event.subject" class="email-subject">
            {{ event.subject }}
        </div>

        <div class="email-body">
            <pre class="email-text">{{ getCleanBody(event.text) }}</pre>
        </div>
    </div>
</template>
